import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Star } from 'lucide-react';
import { DEFAULT_ENTRY_CONFIRMATION_MESSAGE } from '@low6-pickem/style-config';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { renderTextWithLinks } from '@/utils/renderTextWithLinks';
import { withSafeAreaBottom } from './safeArea';

export const PICK_PROGRESS_BAR_HOST_TAB_OFFSET_PX = 72;
export const PICK_PROGRESS_BAR_SCROLL_PADDING_PX = 168;
export const PICK_PROGRESS_BAR_SCROLL_PADDING_WITH_HOST_TAB_PX = 264;
// Vertical space reserved for the in-play sticky footer ad banner.
// AdSlot sizes its slot container with a single `minHeight` of
// `Math.max(...sizes.map(s => s[1]))` — the tallest configured creative height,
// applied at every breakpoint (not a per-breakpoint value). For the standard
// sticky-footer sizes [[320,50],[728,90]] that is 90px everywhere, plus the
// banner's own 8px top/bottom padding => 106px. Used to lift the pinned submit
// CTA above the banner and to extend the picks scroll padding so the last
// question is never hidden behind the banner (DKPLAT-468, retuned DKPLAT-694).
export const PICK_PROGRESS_BAR_STICKY_FOOTER_AD_OFFSET_PX = 106;
// Height of the spacer the INLINE layout renders under the submit button
// (`h-28`). It is the legacy clearance for the DK app's tab bar, sized before
// the overlap was ever measured. When the measured host chrome is larger,
// CompetitionContent tops it up to the measured value; it never stacks a second
// full clearance on top (DKPLAT-744).
export const PICK_PROGRESS_BAR_INLINE_SPACER_PX = 112;

type PickProgressBarLayoutMode = 'inline' | 'pinned';

interface PickProgressBarProps {
  answeredQuestions: boolean[];
  tiebreakerIndices?: number[];
  onSubmit: () => void;
  isSubmitting: boolean;
  className?: string;
  layoutMode?: PickProgressBarLayoutMode;
  bottomOffsetPx?: number;
  /**
   * True when another pinned element (the in-play sticky-footer ad banner)
   * sits below this bar and is itself offset by `env(safe-area-inset-bottom)`.
   * The CTA then has to clear that inset too, and must not pad for it a second
   * time internally — it no longer touches the bottom of the viewport.
   */
  hasStickyFooterBelow?: boolean;
  confirmationLogoUrl?: string;
  /**
   * Whether the tenant runs Pick'em with full navigation (`enableFullNavigation`). Used
   * only as the tenant discriminator for the default confirmation logo: on for SkyPicks,
   * off for DraftKings, and the default asset is SkyPicks-branded.
   */
  fullNavigation?: boolean;
  /**
   * When true a T&C checkbox is shown and must be ticked before submitting.
   * Driven by `pickem.style_config.showTermsCheckbox`.
   */
  showTermsCheckbox?: boolean;
  /**
   * Optional reminder shown above the Submit Picks button.
   * Driven by `pickem.style_config.entry.submit_message`. Hidden when blank.
   */
  submitMessage?: string;
  /**
   * Copy shown in the entry-confirmation popup above the checkbox.
   * Driven by `pickem.style_config.entry.confirmation_message`. Supports
   * markdown-style links (`[label](https://url)`). Falls back to
   * DEFAULT_ENTRY_CONFIRMATION_MESSAGE when blank.
   */
  confirmationMessage?: string;
}

/**
 * PickProgressBar — in-flow progress indicator and submit CTA.
 *
 * Rendered at the bottom of the single-scroll competition page. The whole
 * page (game card → questions → this bar) scrolls as one unit inside the
 * iframe, while the CTA itself stays pinned via `position: sticky`.
 *
 * Confirmation uses a centered Dialog (portaled to document.body) so it
 * always appears in the centre of the iframe viewport, not at the bottom
 * of the page scroll.
 *
 * Submit button is always visible: grey/disabled until all picks are made,
 * green/active once complete.
 */
const PickProgressBar: React.FC<PickProgressBarProps> = ({
  answeredQuestions,
  tiebreakerIndices = [],
  onSubmit,
  isSubmitting,
  className = '',
  layoutMode = 'pinned',
  bottomOffsetPx = 0,
  hasStickyFooterBelow = false,
  confirmationLogoUrl,
  fullNavigation = false,
  showTermsCheckbox = false,
  submitMessage,
  confirmationMessage,
}) => {
  const { t } = useTranslation();
  const confirmationBody =
    typeof confirmationMessage === 'string' && confirmationMessage.trim() !== ''
      ? confirmationMessage.trim()
      : DEFAULT_ENTRY_CONFIRMATION_MESSAGE;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const progressItemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const totalQuestions = answeredQuestions.length;
  const answeredCount = answeredQuestions.filter(Boolean).length;
  const allAnswered = answeredCount === totalQuestions;
  const canSubmit = allAnswered && !isSubmitting;
  const currentProgressIndex = useMemo(() => {
    if (totalQuestions === 0) return 0;
    return Math.min(answeredCount, totalQuestions - 1);
  }, [answeredCount, totalQuestions]);

  /**
   * Submit goes straight through. The entry-confirmation popup used to sit here, but the
   * "Picks Submitted" popup already confirms the entry after the fact, so the two were
   * back-to-back dialogs saying the same thing and the first one earned nothing.
   *
   * The exception is a tenant that requires its T&C checkbox: that popup is the only place
   * the user accepts the terms, so for those tenants it stays as the gate.
   */
  const handleSubmitClick = () => {
    if (!canSubmit) return;
    if (showTermsCheckbox) {
      setShowConfirmation(true);
      return;
    }
    onSubmit();
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setIsAgreed(false);
  };

  const handleConfirm = () => {
    onSubmit();
    handleClose();
  };

  const canConfirm = showTermsCheckbox ? isAgreed && !isSubmitting : !isSubmitting;
  const isPinnedLayout = layoutMode === 'pinned';
  const isLiftedPinnedLayout = isPinnedLayout && bottomOffsetPx > 0;

  // The safe-area inset is honoured exactly once per pinned stack, by whichever
  // element sits closest to the bottom of the viewport. With the sticky-footer
  // ad below us that element is the banner, so we shift up by the inset (to
  // stay clear of the banner, which is itself shifted) and drop it from our own
  // padding. Without the banner we are the bottom-most element and keep the
  // original behaviour: px-only `bottom`, inset absorbed by the padding.
  const pinnedBottom = hasStickyFooterBelow
    ? withSafeAreaBottom(bottomOffsetPx)
    : `${bottomOffsetPx}px`;
  const pinnedPaddingBottom = hasStickyFooterBelow
    ? isLiftedPinnedLayout
      ? '8px'
      : '2px'
    : isLiftedPinnedLayout
      ? 'calc(env(safe-area-inset-bottom) + 8px)'
      : 'max(env(safe-area-inset-bottom), 2px)';

  useEffect(() => {
    if (!isPinnedLayout) {
      return;
    }

    const currentProgressItem = progressItemRefs.current[currentProgressIndex];
    if (!currentProgressItem || typeof currentProgressItem.scrollIntoView !== 'function') {
      return;
    }

    currentProgressItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: answeredCount === 0 ? 'start' : 'nearest',
    });
  }, [answeredCount, currentProgressIndex, isPinnedLayout]);

  const progressTrack = (
    <div
      className={
        isPinnedLayout
          ? 'mb-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          : 'flex items-center justify-center gap-0 mb-2 overflow-x-auto'
      }
      data-test-id="progress-track-viewport"
      data-test-current-index={currentProgressIndex}
    >
      <div
        className={
          isPinnedLayout
            ? 'mx-auto flex w-max flex-nowrap items-center justify-start gap-0 px-1.5 whitespace-nowrap'
            : 'flex items-center justify-center gap-0'
        }
        data-test-id="progress-track"
      >
        {answeredQuestions.map((isAnswered, index) => {
          const isLast = index === totalQuestions - 1;
          const isTiebreaker = tiebreakerIndices.includes(index);
          const circleClass = isAnswered ? 'bg-[#01A925]' : 'bg-[#4B5563]';

          const nextIsAnswered = !isLast && answeredQuestions[index + 1];
          const lineIsGreen = isAnswered && nextIsAnswered;
          const lineClass = lineIsGreen ? 'w-2 h-0.5 bg-[#01A925]' : 'w-2 h-0.5 bg-[#4B5563]';

          return (
            <React.Fragment key={index}>
              <div
                ref={element => {
                  progressItemRefs.current[index] = element;
                }}
                className={
                  isPinnedLayout
                    ? 'flex flex-shrink-0 items-center justify-center w-6 h-6 rounded-full scroll-mx-6 text-[11px] sm:w-7 sm:h-7 sm:text-xs ' +
                      circleClass
                    : 'flex flex-shrink-0 items-center justify-center w-7 h-7 rounded-full ' +
                      circleClass
                }
                data-test-id={'progress-circle-' + (index + 1)}
                data-test-answered={isAnswered}
                data-test-tiebreaker={isTiebreaker}
              >
                {isTiebreaker ? (
                  <Star
                    className={`w-3.5 h-3.5 ${isAnswered ? 'text-white fill-white' : 'text-white'}`}
                  />
                ) : isAnswered ? (
                  <Check className="w-3.5 h-3.5 text-white" />
                ) : (
                  <span className="text-white text-xs font-gilroy font-bold">{index + 1}</span>
                )}
              </div>

              {!isLast && (
                <div
                  className={
                    isPinnedLayout
                      ? `flex-shrink-0 sm:w-2 ${lineClass.replace('w-2', 'w-1.5')}`
                      : `flex-shrink-0 ${lineClass}`
                  }
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {isPinnedLayout ? (
        <div
          className={`sticky bottom-0 z-40 flex w-full justify-center px-3 sm:px-4 ${
            isLiftedPinnedLayout ? 'mt-4' : 'mt-2'
          }`}
          style={{ bottom: pinnedBottom }}
          data-test-id="pick-progress-bar"
          data-test-layout-mode={layoutMode}
          data-test-bottom-offset={bottomOffsetPx}
          data-test-sticky-footer-below={hasStickyFooterBelow}
        >
          <div
            className={
              `bg-app-background border border-border/30 px-3 ${isLiftedPinnedLayout ? 'pt-2.5' : 'pt-2'} w-full rounded-2xl shadow-[0_-6px_18px_rgba(0,0,0,0.45)] ` +
              className
            }
            style={{ paddingBottom: pinnedPaddingBottom }}
          >
            {progressTrack}

            {typeof submitMessage === 'string' && submitMessage.trim() !== '' && (
              <p
                className="text-center text-xs text-[#ABABAB] mb-2"
                data-test-id="submit-reminder-message"
              >
                {submitMessage}
              </p>
            )}

            <Button
              onClick={handleSubmitClick}
              disabled={!canSubmit}
              variant="pickem"
              className={`w-full py-2 text-sm font-gilroy font-semibold rounded-lg transition-colors ${
                canSubmit
                  ? 'bg-[#01A925] hover:bg-[#018A1E] text-white'
                  : 'bg-[#4B5563] text-[#9CA3AF] cursor-not-allowed hover:bg-[#4B5563]'
              }`}
              data-test-id="submit-picks-button"
              data-test-enabled={canSubmit}
            >
              {isSubmitting ? t('actions.submitting') : t('actions.submitPicks')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div
            className="z-40 flex justify-center w-full"
            data-test-id="pick-progress-bar"
            data-test-layout-mode={layoutMode}
          >
            <div
              className={
                'bg-app-background border-t border-border/30 px-4 pt-2 pb-3 w-full shadow-[0_-4px_12px_rgba(0,0,0,0.35)] ' +
                className
              }
            >
              {progressTrack}

              {typeof submitMessage === 'string' && submitMessage.trim() !== '' && (
                <p
                  className="text-center text-xs text-[#ABABAB] mb-2"
                  data-test-id="submit-reminder-message"
                >
                  {submitMessage}
                </p>
              )}

              <Button
                onClick={handleSubmitClick}
                disabled={!canSubmit}
                variant="pickem"
                className={`w-full py-2.5 text-sm font-gilroy font-semibold rounded-lg transition-colors ${
                  canSubmit
                    ? 'bg-[#01A925] hover:bg-[#018A1E] text-white'
                    : 'bg-[#4B5563] text-[#9CA3AF] cursor-not-allowed hover:bg-[#4B5563]'
                }`}
                data-test-id="submit-picks-button"
                data-test-enabled={canSubmit}
              >
                {isSubmitting ? t('actions.submitting') : t('actions.submitPicks')}
              </Button>
            </div>
          </div>

          {/* PICK_PROGRESS_BAR_INLINE_SPACER_PX — keep the class and the constant in step. */}
          <div className="h-28 flex-shrink-0" aria-hidden="true" data-test-id="progress-spacer" />
        </>
      )}

      {/* T&C confirmation — centered popup portaled to document.body so it appears in the
          middle of the iframe viewport regardless of scroll position. Only reachable when
          the tenant requires the terms checkbox; otherwise Submit Picks submits directly. */}
      <Dialog open={showConfirmation} onOpenChange={handleClose}>
        <DialogContent
          data-test-id="confirmation-panel"
          className="bg-[#111111] border-0 rounded-2xl p-0 overflow-hidden shadow-xl w-[90vw] max-w-sm [&>button]:text-white [&>button]:opacity-80 [&>button]:hover:opacity-100"
        >
          <div className="px-6 pt-6 pb-2 flex items-center justify-center">
            <DialogTitle className="text-white font-bold text-xl">Confirmation</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Review your picks and confirm your contest entry.
          </DialogDescription>

          <div className="px-6 pb-6">
            <div className="bg-[#2A2A2A] rounded-xl p-6 mb-4">
              {/*
                A configured logo always renders; the `/skypicks-logo.svg` default only
                under full navigation. That default put SkyPicks branding in a DK tenant's
                confirmation dialog, but removing it outright also took it from SkyPicks,
                where it was correct. `enableFullNavigation` is on for SkyPicks and off for
                DK, so it decides. Same rule as the app header — see AppHeader's note.
              */}
              {(confirmationLogoUrl || fullNavigation) && (
                <div className="flex items-center justify-center mb-4">
                  <img
                    src={confirmationLogoUrl || '/skypicks-logo.svg'}
                    alt="Contest logo"
                    className="h-12"
                  />
                </div>
              )}

              <p
                className="text-white text-center text-sm leading-relaxed whitespace-pre-line"
                data-test-id="confirmation-message"
              >
                {renderTextWithLinks(confirmationBody)}
              </p>

              {showTermsCheckbox && (
                <div className="flex items-start gap-3 mt-4">
                  <button
                    onClick={() => setIsAgreed(!isAgreed)}
                    className="flex-shrink-0 mt-0.5"
                    data-test-id="terms-checkbox"
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isAgreed
                          ? 'bg-[#01A925] border-[#01A925]'
                          : 'bg-transparent border-[#6B7280]'
                      }`}
                    >
                      {isAgreed && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>
                  <label className="text-white text-sm">
                    I have read and agreed to{' '}
                    <a
                      href="https://myaccount.draftkings.com/documents/skypicks-terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pickem-primary underline hover:text-pickem-primary-light"
                      onClick={e => e.stopPropagation()}
                    >
                      SkyPicks contest T&Cs
                    </a>
                  </label>
                </div>
              )}
            </div>

            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className={`w-full py-4 text-base font-gilroy font-bold rounded-xl transition-colors ${
                canConfirm
                  ? 'bg-[#01A925] hover:bg-[#018A1E] text-white'
                  : 'bg-[#4B5563] text-[#9CA3AF] cursor-not-allowed'
              }`}
              data-test-id="confirm-submit-entry"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Entry'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PickProgressBar;
